import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useCreateImapUserMailboxMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

const ImapMailboxFormSchema = z.object({
  name: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
  host: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
  port: z.coerce
    .number({invalid_type_error: i18n.t('general.validations.fieldIsRequired')})
    .int()
    .min(1)
    .max(65535),
  username: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
  password: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
  secure: z.boolean(),
});

type ImapMailboxFormValues = z.infer<typeof ImapMailboxFormSchema>;

const DEFAULT_VALUES: Partial<ImapMailboxFormValues> = {
  port: 993,
  secure: true,
};

export function ImapMailboxForm() {
  const [createImapUserMailbox] = useCreateImapUserMailboxMutation();

  const handleSubmit: FormSubmitHandler<ImapMailboxFormValues> = async (values) => {
    await createImapUserMailbox({
      createUserImapMailboxRequestBody: {
        name: values.name,
        host: values.host,
        port: values.port,
        username: values.username,
        password: values.password,
        secure: values.secure,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(
          i18n.t('page.myProfile.receivingMailbox.notifications.imapConnected')
        );
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<ImapMailboxFormValues>
      defaultValues={DEFAULT_VALUES}
      onSubmit={handleSubmit}
      experimentalZodSchema={ImapMailboxFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            name="name"
            type="text"
            label={i18n.t('entity.mailbox.labels.name')}
            control={control}
            isRequired
          />
          <FormField
            name="host"
            type="text"
            label={i18n.t('page.myProfile.receivingMailbox.imap.host')}
            control={control}
            isRequired
          />
          <Grid columns={2} spacing={4}>
            <FormField
              name="port"
              type="number"
              label={i18n.t('entity.emailService.labels.port')}
              control={control}
              isRequired
            />
            <FormField
              name="username"
              type="text"
              label={i18n.t('entity.emailService.labels.userName')}
              control={control}
              isRequired
            />
          </Grid>
          <FormField
            name="password"
            type="password"
            label={i18n.t('entity.emailService.labels.password')}
            control={control}
            isRequired
          />
          <FormField
            name="secure"
            type="switch"
            label={i18n.t('page.myProfile.receivingMailbox.imap.secure')}
            control={control}
          />
          <Separator />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('page.myProfile.receivingMailbox.connect')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
